import { useEffect, useState } from "react";
import { Row, Col, Stack, Button } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";

import { AppIcon } from "@/components/Icon";
import CircularProgress from "@/components/Common/CircularProgress";
import TableCellChip from "@/components/Table/cell_formats/TableCellChip";
import TableCellCurrency from "@/components/Table/cell_formats/TableCellCurrency";
import TableCellDate from "@/components/Table/cell_formats/TableCellDate";


export default function LoanInformation({ loanUUID, loanSummary }) {
  const navigate = useNavigate();
  const { currentDpd, nextInstallmentDate, principal_outstanding_percentage, totalOutstanding, status } = loanSummary || {};
  const isOverdue = currentDpd > 0;

  return (
    <div className="bg-white rounded-lg p-4" style={{ minHeight: "100%" }}>
      <Stack gap={3}>
        <Row className="align-items-center">
          <Col xs={8} className="text-md fw-semibold text-gray-500">
            Loan Information
          </Col>
          <Col xs={4} className="text-end">
            <span
              className="text-sm text-decoration-underline cursor-pointer"
              onClick={() => navigate(`/lms/${loanUUID}/loan-information`)}
            >
              View All
            </span>
          </Col>
        </Row>

         <Stack gap={4} className="bg-gray-100 p-4 rounded-lg">
          <Stack direction="horizontal" className="justify-between">
            <Stack direction="horizontal" gap={2}>
              <CircularProgress
                percent={Math.round(
                  principal_outstanding_percentage ?? 0
                )}
              />
              <div>
                <div className="text-md fw-semibold text-gray-500">
                  Total Outstanding Amount
                </div>
                <div className="text-3xl">
                  <TableCellCurrency
                    value={totalOutstanding ?? 0}
                    options={{ size: 6 }}
                  />
                </div>
              </div>
            </Stack>
            <TableCellChip value={status ?? "-"} />
          </Stack>

          <hr className="border-gray-200 my-0" />

          <Stack direction="horizontal" className="justify-between">
            <Stack direction="horizontal" gap={2}>
              {isOverdue ? (
                <div className="text-sm fw-semibold text-gray-500">
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <AppIcon
                      name="Error"
                      size={16}
                      color="#e63946"
                    />
                    <span className="text-danger fw-semibold">
                      Payment overdue
                    </span>
                  </div>

                  <div className="ms-3">
                    <div>
                      Your account is overdue by{" "}
                      <b>{currentDpd ?? 0} days</b>
                    </div>

                    <div className="mt-1">
                      Total overdue amount is{" "}
                      <b>
                        <TableCellCurrency
                          value={
                            (loanSummary?.totalOverdueAmount ?? 0) +
                            (loanSummary?.penalDueAmount ?? 0)
                          }
                          size={4}
                        />
                      </b>
                    </div>

                    <div className="text-gray-400 mt-1">
                      Kindly settle the amount as soon as possible. Refer{" "}
                      <a
                        href={`/lms/${loanUUID}/loan-information`}
                        className="text-decoration-underline"
                      >
                        Loan information
                      </a>{" "}
                      page for details.
                    </div>
                  </div>
                </div>
              ) : nextInstallmentDate ? (
                <>
                  <div className="bg-blue-100 text-blue-500 p-3 rounded-circle">
                    <AppIcon name="HourGlass" />
                  </div>

                  <div className="text-sm fw-semibold text-gray-500">
                    Next installment is due on
                    <br />
                    <b>
                      <TableCellDate
                        value={nextInstallmentDate}
                        options={{ formatType: "long" }}
                      />
                    </b>{" "}
                    for an amount of{" "}
                    <b>
                      <TableCellCurrency
                        value={loanSummary?.installmentAmount ?? 0}
                        size={4}
                      />
                    </b>
                  </div>
                </>
              ) : (
                <>
                  <div className="bg-green-100 text-green-500 p-3 rounded-circle">
                    <AppIcon name="CheckCircle" />
                  </div>

                  <div className="text-sm fw-semibold text-gray-500">
                    No upcoming installments
                    <br />
                    <span className="text-gray-400">
                      Your loan is fully settled. No dues are pending.
                    </span>
                  </div>
                </>
              )}
            </Stack>
          </Stack>
        </Stack>

        <Row className="g-4">
          <Col md={3}>
            <div className="text-sm text-gray-500">Original Amount</div>
            <TableCellCurrency
              value={loanSummary?.sanctionedAmount ?? 0}
              size={4}
            />
          </Col>

          <Col md={3}>
            <div className="text-sm text-gray-500">Disbursement Date</div>
            <TableCellDate
              value={loanSummary?.firstDisbursmentDate}
              options={{ formatType: "long" }}
            />
          </Col>

          <Col md={3}>
            <div className="text-sm text-gray-500">Tenure</div>
            <div className="fw-semibold">
              {loanSummary?.originalTenor ?? "-"} Months
            </div>
          </Col>

          <Col md={3}>
            <div className="text-sm text-gray-500">Int_Fee Rate</div>
            <div className="fw-semibold">
              {loanSummary?.interestRate ?? "-"}%
            </div>
          </Col>
        </Row>
      </Stack>
    </div>
  );
}
